import React, { useEffect, useRef, useState } from 'react';

import { FilterRange, Input, Option, Toggle } from '@hcs/design-system';
import { TableCell, TableHeaderCell } from '@hcs/design-system';
import {
  PropertyStateFilterProps,
  TableCellProps,
  TableHeaderCellProps,
} from '@hcs/types';
import { SpatialSortField } from '@hcs/types';
import {
  PropertyStateArgsProps,
  PropertyStateEditCallbackArgs,
  PropertyStateFieldConfig,
  PropertyStateFields,
  PropertyStatePaths,
  PropertyStateTableCellProps,
  PropertyStateTableHeaderCellProps,
} from '@hcs/types';
import { numInputStrStrip } from '@hcs/utils';
import {
  acresToSqft,
  formatNumber,
  formatNumberAbbrev,
  formatSqFt,
  SQFT_PER_ACRE,
  sqftToAcres,
  unformat,
} from '@hcs/utils';

import styles from './lotSize.module.css';

const FIELD = PropertyStateFields.lotSize;
type FieldConfig = PropertyStateFieldConfig<typeof FIELD>;
const label = 'Lot Size';
const labelShort = 'Lot Size';
const propertyStatePath: PropertyStatePaths = '/propertyDetails/lotSize';
const getValue: FieldConfig['getValue'] = (propertyStateArgs) => {
  const { propertyState, propertyStateType } = propertyStateArgs || {};
  if (propertyStateType === 'flat') {
    return propertyState?.[FIELD];
  }
  return propertyState?.propertyDetails?.lotSize;
};

const LOT_SIZE_TOGGLE_OPTIONS: Option<boolean>[] = [
  {
    label: 'Sq Ft',
    value: true,
  },
  {
    label: 'Acres',
    value: false,
  },
];

const formatValue: FieldConfig['formatValue'] = (propertyStateArgs) =>
  formatSqFt(getValue(propertyStateArgs));

// For displaying multiple fields in a single line
const labelMicro: FieldConfig['labelMicro'] = 'Lot ft²';
const formatValueShort: FieldConfig['formatValueShort'] = (propertyStateArgs) =>
  formatNumberAbbrev(getValue(propertyStateArgs));

const formatInline: FieldConfig['formatInline'] = (propertyStateArgs) =>
  `${formatValue(propertyStateArgs)} Lot`;
// Component for displaying a field from a schema
// eslint-disable-next-line react-refresh/only-export-components
const Display = ({
  propertyStateArgs,
}: PropertyStateArgsProps & {
  includeUnit?: boolean;
}) => {
  return <>{formatValue(propertyStateArgs)}</>;
};

// eslint-disable-next-line react-refresh/only-export-components
const Edit = ({
  propertyStateArgs,
  onChange,
  className,
}: PropertyStateArgsProps & {
  onChange: (args: PropertyStateEditCallbackArgs<typeof FIELD>) => void;
  className?: string;
}) => {
  const defaultValue = getValue(propertyStateArgs);
  const [value, setValue] = useState(
    defaultValue === null ? null : formatNumber(defaultValue),
  );

  return (
    <Input
      dataHcName="lot-size-adjust-input"
      ariaLabel={`Adjust ${label}`}
      className={className}
      value={value?.toString() || ''}
      onChange={(value) => {
        const valueOrNull = value === '' ? null : numInputStrStrip(value);
        setValue(valueOrNull);
        onChange({
          path: propertyStatePath,
          value: valueOrNull === null ? valueOrNull : Number(valueOrNull),
          field: FIELD,
        });
      }}
      maxLength={25}
      onFocus={() => {
        if (value !== null) {
          setValue(unformat(value).toString());
        }
      }}
      onBlur={() => {
        if (value !== null) {
          setValue(formatNumber(Number(value)));
        }
      }}
    />
  );
};

// eslint-disable-next-line react-refresh/only-export-components
const HeaderCell = (props: PropertyStateTableHeaderCellProps) => {
  return <TableHeaderCell {...props}>{labelShort} (ft²)</TableHeaderCell>;
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultHeaderProps: Partial<TableHeaderCellProps> = {
  align: 'right',
  // Needed so child component type-checking passes
  isTableHeaderCell: true,
};
HeaderCell.defaultProps = defaultHeaderProps;

// eslint-disable-next-line react-refresh/only-export-components
const ContentCell = ({
  propertyStateArgs,
  ...tableCellProps
}: PropertyStateTableCellProps) => {
  return (
    <TableCell {...tableCellProps}>
      {formatNumber(getValue(propertyStateArgs))}
    </TableCell>
  );
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultContentProps: Partial<TableCellProps> = {
  align: 'right',
  // Needed so child component type-checking passes
  isTableCell: true,
};
ContentCell.defaultProps = defaultContentProps;

/**
 * Holds the value the user actually typed, in the unit they typed it in.
 * Only one of sqft / acres is set at a time — whichever was entered last.
 * This prevents conversion rounding when the user toggles units.
 */
interface LotSizeValue {
  sqft: number | null; // set when user typed in sq ft mode
  acres: number | null; // set when user typed in acres mode
}

/** Convert a LotSizeValue to the sq ft integer the API expects.
 * If v.sqft is set (user typed in sq ft mode) → returns it directly, no conversion needed
 * If v.sqft is null but v.acres is set (user typed in acres mode) → converts acres → sq ft using acresToSqft
 * If both are null (field is empty) → returns null
 * */
const toApiSqft = (v: LotSizeValue): number | null =>
  v.sqft ?? (v.acres != null ? acresToSqft(v.acres) : null);

// eslint-disable-next-line react-refresh/only-export-components
const Filter = ({
  spatialFiltersInput,
  onChange,
  className,
  disabled,
  hideLabels,
}: PropertyStateFilterProps<typeof FIELD>) => {
  const [measurementType, setMeasurementType] = useState(true); // true = sq ft, false = acres

  // Store both units independently — no conversion loss when toggling.
  const [minVal, setMinVal] = useState<LotSizeValue>({
    sqft: spatialFiltersInput.minLotArea ?? null,
    acres: null,
  });
  const [maxVal, setMaxVal] = useState<LotSizeValue>({
    sqft: spatialFiltersInput.maxLotArea ?? null,
    acres: null,
  });

  // Track the last sq ft values we sent to the API so we can ignore
  // the echoed prop update from our own onBlur.
  const committedRef = useRef<{ min: number | null; max: number | null }>({
    min: spatialFiltersInput.minLotArea ?? null,
    max: spatialFiltersInput.maxLotArea ?? null,
  });

  // Remount FilterRange only when values change externally (e.g. Clear All Filters).
  const [clearKey, setClearKey] = useState(0);

  useEffect(() => {
    const newMin = spatialFiltersInput.minLotArea ?? null;
    const newMax = spatialFiltersInput.maxLotArea ?? null;
    if (
      newMin !== committedRef.current.min ||
      newMax !== committedRef.current.max
    ) {
      committedRef.current = { min: newMin, max: newMax };
      setMinVal({ sqft: newMin, acres: null });
      setMaxVal({ sqft: newMax, acres: null });
      setClearKey((k) => k + 1);
    }
  }, [spatialFiltersInput.minLotArea, spatialFiltersInput.maxLotArea]);

  /**
   * Display the value in the current unit.
   * Prefer the natively-typed value for the active unit so no conversion is applied.
   * Fall back to converting the other unit only when necessary.
   */
  const toDisplay = (v: LotSizeValue): string => {
    if (measurementType) {
      // Sq ft mode — prefer stored sqft, convert from acres only as fallback
      const sqft = v.sqft ?? (v.acres != null ? acresToSqft(v.acres) : null);
      return sqft != null ? formatNumber(sqft) : '';
    } else {
      // Acres mode — prefer stored acres (shows exactly what user typed), convert sqft as fallback
      const acres = v.acres ?? (v.sqft != null ? sqftToAcres(v.sqft) : null);
      return acres != null ? acres.toFixed(2) : '';
    }
  };

  const parseInput = (str: string | undefined): number | null => {
    if (!str || str === '') return null;
    const num = Number(str.replace(/,/g, ''));
    return isNaN(num) ? null : num;
  };

  return (
    <div className={styles.LotSizeFilter}>
      <Toggle<boolean>
        primary
        equalWidthOptions={true}
        dataHcName="lot-size-adjust-measurement-type"
        value={measurementType}
        theme={{
          Option: styles.Option,
          SelectedMask: styles.Option,
        }}
        options={LOT_SIZE_TOGGLE_OPTIONS}
        onChange={() => setMeasurementType(!measurementType)}
      />
      <FilterRange
        key={`${measurementType}-${clearKey}`}
        className={className}
        disabled={disabled}
        decimal={!measurementType}
        hideLabels={hideLabels}
        placeHolders={{
          min: measurementType ? 'Min sf' : 'Min ac',
          max: measurementType ? 'Max sf' : 'Max ac',
        }}
        maxVal={
          measurementType ? 99999999 : Math.round(99999999 / SQFT_PER_ACRE)
        }
        initialValue={{
          min: toDisplay(minVal),
          max: toDisplay(maxVal),
        }}
        onBlur={(value) => {
          const rawMin = parseInput(value.min);
          const rawMax = parseInput(value.max);

          const newMinVal: LotSizeValue = measurementType
            ? { sqft: rawMin != null ? Math.round(rawMin) : null, acres: null }
            : { sqft: null, acres: rawMin };
          const newMaxVal: LotSizeValue = measurementType
            ? { sqft: rawMax != null ? Math.round(rawMax) : null, acres: null }
            : { sqft: null, acres: rawMax };

          const apiMin = toApiSqft(newMinVal);
          const apiMax = toApiSqft(newMaxVal);

          committedRef.current = { min: apiMin, max: apiMax };
          setMinVal(newMinVal);
          setMaxVal(newMaxVal);
          onChange({
            field: FIELD,
            spatialFilterInputs: {
              minLotArea: apiMin,
              maxLotArea: apiMax,
            },
          });
        }}
        dataHcName={`${FIELD}-filter`}
      />
    </div>
  );
};

export const LOT_SIZE_CONFIG: FieldConfig = {
  field: FIELD,
  label,
  labelShort,
  propertyStatePath,
  getValue,
  formatValue,
  labelMicro,
  formatValueShort,
  formatInline,
  HeaderCell,
  ContentCell,
  Display,
  Edit,
  Filter,
  spatialSortField: SpatialSortField.LotArea,
};
